import { useState } from "react";
import { X, User as UserIcon, Lock, ShieldCheck, Check } from "lucide-react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { useAuth } from "@/lib/auth";

// Self-service Profile edit modal (2026-08-01).
//
// Available to every authenticated user regardless of role. Reached from the
// sidebar footer where the user's name is shown. Two independent flows,
// both operating on the caller's own record:
//
//   1. Name change — PATCH /api/auth/me/profile
//   2. Password change — POST /api/auth/me/change-password (requires
//      current_password re-entry per the industry-standard "still-me" gate)
//
// The role field is deliberately read-only on this surface — no user, no
// matter their role, can change their own role. Role changes remain gated on
// the admin flow (PATCH /api/users/{id}, owner/platform_admin only).

const ROLE_LABEL = {
  platform_admin: "Platform admin",
  owner: "Owner",
  administrative: "Administrative",
  call_center: "Call center / CSR",
  technician: "Technician",
};

const INPUT_CLS = "w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc]";
const LABEL_CLS = "text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className={LABEL_CLS}>{label}</span>
        {hint && <span className="text-[11px] text-[color:var(--cc-ink-muted)] font-normal">{hint}</span>}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function ProfileEditModal({ onClose }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [nameSavedAt, setNameSavedAt] = useState(0);
  const [nameError, setNameError] = useState(null);

  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwSavedAt, setPwSavedAt] = useState(0);
  const [pwError, setPwError] = useState(null);

  const nameDirty = (name || "").trim() !== (user?.name || "");
  const pwFormValid = current.length > 0 && pw.length >= 8 && pw === pw2;

  const submitName = async (e) => {
    e.preventDefault();
    if (!nameDirty) return;
    setSavingName(true);
    setNameError(null);
    try {
      await api.patch("/auth/me/profile", { name: name.trim() });
      // Refresh auth context so the sidebar footer name updates immediately.
      if (typeof refresh === "function") await refresh();
      setNameSavedAt(Date.now());
    } catch (err) {
      setNameError(extractApiError(err)?.message || "Could not update name");
    } finally {
      setSavingName(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pwFormValid) return;
    setSavingPw(true);
    setPwError(null);
    try {
      await api.post("/auth/me/change-password", {
        current_password: current,
        new_password: pw,
      });
      setPwSavedAt(Date.now());
      setCurrent("");
      setPw("");
      setPw2("");
    } catch (err) {
      setPwError(extractApiError(err)?.message || "Could not change password");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="profile-edit-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-[560px] shadow-xl border border-[#e7e9ee] my-8"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e7e9ee]">
          <div className="flex items-center gap-2.5">
            <UserIcon size={18} strokeWidth={1.8} className="text-[#0072bc]" />
            <div>
              <h2 className="font-display font-bold text-[17px] text-[#0e1420]">My profile</h2>
              <p className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5">Update your name and password. Role is set by an owner.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-[#f5f6f8] rounded-md"
            data-testid="profile-edit-modal-close"
          >
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        {/* Identity summary — email + role are read-only. */}
        <div className="px-5 pt-5">
          <div className="bg-[#f9fafc] border border-[#eef1f6] rounded-lg p-4 space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className={LABEL_CLS}>Email</span>
              <span className="text-[13px] font-mono-data text-[#0e1420]" data-testid="profile-email">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className={LABEL_CLS}>Role</span>
              <span
                data-testid="profile-role-readonly"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-mono-data uppercase tracking-widest bg-white text-[color:var(--cc-ink-muted)] border border-[#e7e9ee] rounded px-2 py-0.5"
                title="Role changes are set by an owner or platform admin."
              >
                <ShieldCheck size={11} strokeWidth={2} />
                {ROLE_LABEL[user?.role] || user?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Name edit */}
        <form onSubmit={submitName} className="p-5 border-b border-[#eef1f6]">
          <Field label="Display name">
            <input
              data-testid="profile-field-name"
              className={INPUT_CLS}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
              autoComplete="name"
            />
          </Field>
          {nameError && (
            <div data-testid="profile-name-error" className="mt-2 text-[12px] text-[#c73535]">{nameError}</div>
          )}
          <div className="mt-3 flex items-center justify-end gap-3">
            {nameSavedAt > 0 && !nameDirty && (
              <span data-testid="profile-name-saved" className="text-[12px] text-[#0e8a68] inline-flex items-center gap-1">
                <Check size={13} strokeWidth={2.2} /> Saved
              </span>
            )}
            <button
              type="submit"
              disabled={!nameDirty || savingName}
              data-testid="profile-save-name-btn"
              className={`text-[13px] font-semibold py-2 px-3 rounded-md ${
                !nameDirty || savingName
                  ? "bg-[#eef1f6] text-[color:var(--cc-ink-muted)] cursor-not-allowed"
                  : "bg-[#0072bc] text-white hover:bg-[#005a94]"
              }`}
            >
              {savingName ? "Saving..." : "Save name"}
            </button>
          </div>
        </form>

        {/* Password change */}
        <form onSubmit={submitPassword} className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={14} strokeWidth={1.8} className="text-[#0072bc]" />
            <h3 className="font-display font-semibold text-[14px] text-[#0e1420]">Change password</h3>
          </div>
          <div className="space-y-3">
            <Field label="Current password">
              <input
                type="password"
                data-testid="profile-field-current-password"
                className={INPUT_CLS}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
              />
            </Field>
            <Field label="New password" hint="Min 8 chars, must include a number">
              <input
                type="password"
                data-testid="profile-field-new-password"
                className={INPUT_CLS}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirm new password">
              <input
                type="password"
                data-testid="profile-field-confirm-password"
                className={INPUT_CLS}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password"
              />
              {pw2.length > 0 && pw !== pw2 && (
                <div data-testid="profile-password-mismatch" className="mt-1 text-[11.5px] text-[#c73535]">Passwords do not match</div>
              )}
            </Field>
          </div>
          {pwError && (
            <div data-testid="profile-password-error" className="mt-2 text-[12px] text-[#c73535]">{pwError}</div>
          )}
          <div className="mt-3 flex items-center justify-end gap-3">
            {pwSavedAt > 0 && (
              <span data-testid="profile-password-saved" className="text-[12px] text-[#0e8a68] inline-flex items-center gap-1">
                <Check size={13} strokeWidth={2.2} /> Password updated
              </span>
            )}
            <button
              type="submit"
              disabled={!pwFormValid || savingPw}
              data-testid="profile-save-password-btn"
              className={`text-[13px] font-semibold py-2 px-3 rounded-md ${
                !pwFormValid || savingPw
                  ? "bg-[#eef1f6] text-[color:var(--cc-ink-muted)] cursor-not-allowed"
                  : "bg-[#0072bc] text-white hover:bg-[#005a94]"
              }`}
            >
              {savingPw ? "Saving..." : "Change password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
